import React, { CSSProperties, useEffect, useRef } from 'react';
import styled from '@emotion/styled';

type ClockRenderingContext = {
    running: boolean;
    html: React.RefObject<HTMLElement>;
    handle?: number;
}

type ClockSurfaceProps = {
    fontSize?: CSSProperties['fontSize'];
}

const ClockSurface = styled.div<ClockSurfaceProps>(p => ({
    height: '100%',
    width: '100%',
    fontSize: p.fontSize ?? 'xxx-large',
    textAlign: 'center',
}));

export type WallClockProps = ClockSurfaceProps & {
    renderer?: (now: Date, html: HTMLDivElement, defaultRenderer: (now: Date, html: HTMLDivElement) => void) => void;
};

export function WallClock(props: WallClockProps) {
    const { renderer, ...others } = props;

    const clock = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const ctx: ClockRenderingContext = { running: true, html: clock };

        function defaultRenderer(now: Date, html: HTMLDivElement) {
            const h = now.getHours().toString().padStart(2, '0');
            const m = now.getMinutes().toString().padStart(2, '0');
            const s = now.getSeconds().toString().padStart(2, '0');
            const ms = now.getMilliseconds().toString().padStart(3, '0');
            html.innerText = `${h}:${m}:${s}.${ms}`;
        }

        const _renderer = renderer ?? defaultRenderer;

        function animate() {
            ctx.handle = undefined;
            if (!ctx.running) return;
            if (!clock.current) return;
            const now = new Date(Date.now());
            _renderer(now, clock.current, defaultRenderer);
            ctx.handle = requestAnimationFrame(animate);
        }

        ctx.handle = requestAnimationFrame(animate);
        return () => {
            ctx.running = false;
            if (ctx.handle !== undefined) {
                cancelAnimationFrame(ctx.handle);
            }
        };
    }, [renderer]);

    return (
        <ClockSurface ref={clock} {...others} />
    );
}
